import { useMemo } from "react";
import jwtDecode from "jwt-decode";
import GetCookieByName from "./GetCookie";

// Decoded once from the auth cookie's JWT — same pattern already used in
// layouts/CampaignsDashboard.jsx and components/payments/CategoryCards.jsx — so gating
// the "Access Management" nav item / route, or hiding a section a Simple User hasn't
// been granted, never needs an extra round trip.
//
// Role/permission changes made from Access Management take effect server-side
// immediately (middleware/auth.js re-reads the DB on every request); an already-open tab
// only picks up the new decoded values on its next login, same as every other
// isAdmin/isSuperUser check in this app.
export default function useAccess() {
  return useMemo(() => {
    try {
      const token = JSON.parse(GetCookieByName("auth"))["token"];
      const decoded = jwtDecode(token) || {};
      const isSuperUser = Boolean(decoded.isSuperUser);
      const isAdmin = Boolean(decoded.isAdmin) || isSuperUser;
      const role = isSuperUser ? "superadmin" : isAdmin ? "admin" : "user";
      const hasPermissionsClaim = Array.isArray(decoded.permissions);
      const permissions = hasPermissionsClaim ? decoded.permissions : [];

      return {
        role,
        isSuperUser,
        isAdmin,
        permissions,
        // The token's `permissions` claim is already the fully-resolved effective list
        // (helpers/accessControl.js#effectivePermissions on the backend) — everyone gets
        // every key in it unless a Super Admin has explicitly pruned them, so this must
        // trust it literally rather than re-granting blanket access on top for isAdmin (that
        // would make pruning an Admin's sections a no-op).
        // No claim at all (an older token, or this shipping before the backend that mints it
        // has) must default to full access for every role, not just isAdmin — the backend's
        // own default is "nobody loses access as a side effect of this feature existing", and
        // the frontend must fail the same direction, not fail closed for every plain User.
        hasPermission: (key) => (hasPermissionsClaim ? permissions.includes(key) : true),
      };
    } catch (error) {
      return { role: "user", isSuperUser: false, isAdmin: false, permissions: [], hasPermission: () => false };
    }
  }, []);
}
